import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import * as api from '../../api';
import { Btn, RNLLoader } from '../UI';
import { NAVY, exportXlsx, fmtDate } from './mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '10px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)' };

const COLS = [
  { key: 'project_code', label: 'Project Code' }, { key: 'project_name', label: 'Project Name' }, { key: 'location', label: 'Location' },
  { key: 'buildings', label: 'Buildings' }, { key: 'total_lines', label: 'Total BOQ Lines' }, { key: 'mapped', label: 'Mapped' },
  { key: 'partial', label: 'In Progress' }, { key: 'unmapped', label: 'Unmapped' }, { key: 'mapping_pct', label: 'Mapping %' },
  { key: 'last_updated', label: 'Last Updated', value: r => fmtDate(r.last_updated) },
];

// BOQ-side mapping control: for every project, how much of its BOQ is mapped.
export default function MMProjects({ projectId, onOpenProject, toast }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.listMappingProjects().then(r => setRows(r || []))
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load projects', 'error'); });
  }, [toast]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>MAP BOQs</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            BOQ-side mapping control — for every project: how many current BOQ lines are mapped, in progress or not started.
          </p>
        </div>
        <Btn variant="secondary" disabled={!rows?.length} onClick={() => exportXlsx(rows, COLS, 'MAP-BOQs', 'Projects')}><Download size={14} /> Export Excel</Btn>
      </div>
      {rows === null ? <RNLLoader title="Loading projects..." subtitle="Counting BOQ lines and mappings" /> : (
        <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['#', 'Project Code', 'Project Name', 'Location', 'Buildings', 'Total BOQ Lines', 'Mapped', 'In Progress', 'Unmapped', 'Mapping %', 'Last Updated', 'Action'].map(h => (
                <th key={h} style={{ ...th, textAlign: ['Buildings', 'Total BOQ Lines', 'Mapped', 'In Progress', 'Unmapped'].includes(h) ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={12} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No project has BOQ lines yet.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.project_id} style={{ background: String(r.project_id) === String(projectId) ? 'rgba(11,49,139,0.05)' : undefined }}>
                  <td style={td}>{i + 1}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: NAVY }}>{r.project_code || '—'}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.project_name}</td>
                  <td style={td}>{r.location || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.buildings}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{r.total_lines}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#166534', fontWeight: 700 }}>{r.mapped}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#a16207' }}>{r.partial}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#b91c1c' }}>{r.unmapped}</td>
                  <td style={{ ...td, minWidth: 150 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--bg-base)', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
                        <div style={{ width: `${r.mapping_pct}%`, height: '100%', background: '#16a34a' }} />
                      </div>
                      <b style={{ fontSize: 12 }}>{r.mapping_pct}%</b>
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.last_updated)}</td>
                  <td style={td}><Btn style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => onOpenProject(r.project_id)}>Open Project</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
